import { EventEmitter, Inject, Injectable } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-draw';
import { ToastrService } from 'ngx-toastr';
import { Command } from '../../core/types/command.enum';
import { AUTH_PROVIDER, NAVBAR_PROVIDER, PROJECT_PROVIDER, CONFIRM_SERVICE } from '../../core/tokens';
import { AuthProvider, NavbarProvider, ProjectProvider, IConfirmService } from '../../core/interfaces';
import { GpsUpdateTimer } from '../utils';
import { DrawStyleService } from './draw-style.service';
import { LayerService } from './layer.service';
import { MapSelectionService } from './map-selection.service';
import { LayerOrderingService } from './layer-ordering.service';
import { ZeichnungslayerDefinition } from '../types/zeichnungslayer.types';
import { LatLng } from 'leaflet';

/**
 * Bridge-Interface zur Kopplung zwischen DrawingService und MapComponent.
 * Nur für Funktionalität die direkt vom MapComponent bereitgestellt wird.
 */
export interface DrawingBridge {
	addLayerToMap(name: string, layer: any, active: boolean): void;
	getCurrentPositionLatLng(): L.LatLng;
}

/**
 * Service für alle Zeichen-Funktionalitäten der Karte.
 * Verwaltet Polygon-, Linien-, Freihand-, GPS-Tracking-Zeichenmodi,
 * Edit/Delete-Werkzeuge und Zeichnungslayer-CRUD.
 */
@Injectable({ providedIn: 'root' })
export class DrawingService {

	// ==================== REFERENZEN ====================
	private map!: L.Map;
	private bridge!: DrawingBridge;

	// ==================== DRAWING MODE FLAGS ====================
	drawEnabled = false;
	drawPolygonPoints = 0;
	freehandEnabled = false;
	lineEnabled = false;
	lineFreehandEnabled = false;
	editEnabled = false;
	deleteEnabled = false;
	gpsTrackingPolygonEnabled = false;
	gpsTrackingLineEnabled = false;
	hasDrawnPolygon = false;
	hasDrawnLine = false;
	newPointOnMapActive = false;

	/** Callback wird aufgerufen wenn ein Zeichen-/Edit-Tool aktiviert wird (für Tool-Exklusivität) */
	onToolActivated?: () => void;

	// Pending-Tool: Merkt sich welches Tool aktiviert werden soll, sobald ein Zeichnungslayer aktiviert wird
	private pendingDrawTool: 'draw' | 'freehand' | 'line' | 'lineFreehand' | 'gpsTrackingPolygon' | 'gpsTrackingLine' | null = null;

	// ==================== FREEHAND POLYGON STATE ====================
	private freehandDrawing = false;
	private freehandPoints: L.LatLng[] = [];
	private freehandPolyline?: L.Polyline;
	private lastFreehandPoint?: L.LatLng;
	private readonly FREEHAND_MIN_DISTANCE = 15; // Pixel

	// ==================== LINE FREEHAND STATE ====================
	private lineHandler?: any;
	private lineFreehandDrawing = false;
	private lineFreehandPoints: L.LatLng[] = [];
	private lineFreehandPolyline?: L.Polyline;
	private lastLineFreehandPoint?: L.LatLng;

	// ==================== DELETE STATE ====================
	private deleteClickHandler: ((e: L.LeafletMouseEvent) => void) | null = null;
	private deleteMouseoverHandler: ((e: L.LeafletMouseEvent) => void) | null = null;
	private deleteMoveHandler: ((e: L.LeafletMouseEvent) => void) | null = null;
	private deleteMousedownHandler: ((e: L.LeafletMouseEvent) => void) | null = null;
	private deleteMouseupHandler: (() => void) | null = null;
	private deleteMouseleaveHandler: (() => void) | null = null;
	private deleteTouchStartHandler: ((e: TouchEvent) => void) | null = null;
	private deleteTouchMoveHandler: ((e: TouchEvent) => void) | null = null;
	private deleteTouchEndHandler: (() => void) | null = null;
	private deleteMouseDown = false;
	private deleteTrail: L.Polyline | null = null;
	private deleteTrailPoints: L.LatLng[] = [];
	private deletedLayersStack: { layer: any; isLine: boolean; featureGroupId: string | null }[] = [];
	private deleteActiveLayerId: string | null = null;

	// ==================== ZEICHNUNGSLAYER STATE ====================
	private zeichnungen = new Map<string, L.FeatureGroup>();
	zeichnungenLinien?: L.FeatureGroup;
	zeichnungenPolygone?: L.FeatureGroup;
	private activeZeichnungslayerId: string | null = null;
	private zeichnungslayerCache: ZeichnungslayerDefinition[] = [];
	private isSavingZeichnungslayer = false;

	// ==================== DRAW HANDLER STATE ====================
	private drawHandler?: any;
	private editHandler?: any;
	private deleteHandler?: any;
	private drawEventsRegistered = false;

	// ==================== GPS TRACKING STATE ====================
	private wakeLock: WakeLockSentinel | null = null;
	currentTrackPolyline?: L.Polyline;
	tmpTrackOffset = 0;
	private gpsUpdateTimer?: GpsUpdateTimer;
	private gpsTrackWasInterrupted = false;

	// ==================== NEW POINT STATE ====================
	private newPointOnMapClickHandler?: (e: any) => void;
	private zeichnungClickInterceptor = (e: L.LeafletMouseEvent) => {
		if (this.newPointOnMapActive) {
			L.DomEvent.stopPropagation(e);
			(e.target as any).closePopup?.();
			if (this.newPointOnMapClickHandler) {
				this.newPointOnMapClickHandler(e);
			}
		}
	};

	// Getter für aktiven Zeichnungslayer (Kompatibilität)
	get polygons(): L.FeatureGroup | undefined {
		return this.activeZeichnungslayerId
			? this.zeichnungen.get(this.activeZeichnungslayerId)
			: undefined;
	}

	/** Prüft ob ein Schlüssel zu einem Zeichnungslayer gehört */
	hasZeichnungslayer(id: string): boolean {
		return this.zeichnungen.has(id);
	}

	/** Gibt alle Zeichnungslayer-IDs zurück */
	getZeichnungslayerIds(): IterableIterator<string> {
		return this.zeichnungen.keys();
	}

	constructor(
		private ls: LayerService,
		@Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
		@Inject(AUTH_PROVIDER) private aus: AuthProvider,
		@Inject(NAVBAR_PROVIDER) private ns: NavbarProvider,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		private msg: ToastrService,
		private drawStyleService: DrawStyleService,
		private mapSelectionService: MapSelectionService,
		private layerOrderingService: LayerOrderingService
	) {}

	// ==================== INITIALISIERUNG ====================

	/**
	 * Initialisiert den Service mit Map-Referenz und Bridge.
	 * Wird von MapComponent in onMapReady aufgerufen.
	 */
	init(map: L.Map, bridge: DrawingBridge): void {
		this.map = map;
		this.bridge = bridge;

		// Visibility-Change-Handler registrieren
		document.addEventListener('visibilitychange', this.onVisibilityChange);
	}

	/**
	 * Initialisiert Zeichnungslayer-Subscriptions.
	 * Wird separat vom init() aufgerufen, da MapComponent die Subscriptions
	 * im Constructor registriert, bevor die Map bereit ist.
	 */
	initSubscriptions(): void {
		this.ls.zeichnungslayerEditRequested.subscribe((layerId: string | null) => {
			if (layerId) {
				this.startZeichnungslayerEdit(layerId);
			} else {
				this.stopZeichnungslayerEdit();
			}
		});
		this.ls.zeichnungslayerChanged.subscribe(() => this.updateZeichnungslayerCache());
		this.ls.zeichnungslayerReloadRequested.subscribe(() => this.loadZeichnungslayer());
		this.ls.zeichnungslayerVisibilityChanged.subscribe((layerId: string) => this.toggleZeichnungslayerVisibility(layerId));
		this.ls.zeichnungslayerZoomRequested.subscribe((layerId: string) => this.zoomToZeichnungslayer(layerId));

		// Wenn ein Zeichnungslayer aktiviert/deaktiviert wird
		let previousLayerId: string | null = null;
		// Angular effect() kann hier nicht verwendet werden, da DrawingService kein Component ist.
		// Stattdessen wird der aktive Layer über checkActiveZeichnungslayer() geprüft.
	}

	/**
	 * Räumt den Service auf. Wird von MapComponent in ngOnDestroy aufgerufen.
	 */
	destroy(): void {
		this.endAllDrawModes();
		this.gpsUpdateTimer?.destroy();
		this.gpsUpdateTimer = undefined;
		this.releaseWakeLock();
		document.removeEventListener('visibilitychange', this.onVisibilityChange);
	}

	// ==================== PENDING TOOL ====================

	/**
	 * Wird von MapComponent aufgerufen wenn der aktive Zeichnungslayer sich ändert.
	 */
	onActiveDrawLayerChanged(activeLayerId: string | null, previousLayerId: string | null): void {
		if (activeLayerId && this.pendingDrawTool) {
			const tool = this.pendingDrawTool;
			this.pendingDrawTool = null;
			setTimeout(() => this.activatePendingTool(tool), 0);
		} else if (!activeLayerId && previousLayerId) {
			setTimeout(() => this.endAllDrawModes(), 0);
		}
	}

	private activatePendingTool(tool: 'draw' | 'freehand' | 'line' | 'lineFreehand' | 'gpsTrackingPolygon' | 'gpsTrackingLine'): void {
		switch (tool) {
			case 'draw':
				this.activateDrawMode();
				break;
			case 'freehand':
				this.activateFreehandMode();
				break;
			case 'line':
				this.activateLineMode();
				break;
			case 'lineFreehand':
				this.activateLineFreehandMode();
				break;
			case 'gpsTrackingPolygon':
				this.activateGpsTrackingPolygonMode();
				break;
			case 'gpsTrackingLine':
				this.activateGpsTrackingLineMode();
				break;
		}
	}

	endAllDrawModes(): void {
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.onStopGpsTracking();
		this.pendingDrawTool = null;
	}

	/**
	 * Prüft ob irgendein Zeichen-, Edit- oder Delete-Modus aktiv ist.
	 */
	isAnyDrawModeActive(): boolean {
		return this.drawEnabled || this.freehandEnabled || this.lineEnabled ||
			this.lineFreehandEnabled || this.editEnabled || this.deleteEnabled ||
			this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled;
	}

	/**
	 * Aktualisiert das isDrawToolActive-Signal im LayerService basierend auf den aktuellen Tool-Flags.
	 */
	private updateDrawToolActiveState(): void {
		const isActive = this.drawEnabled || this.freehandEnabled || this.lineEnabled ||
			this.lineFreehandEnabled || this.editEnabled || this.deleteEnabled;
		this.ls.isDrawToolActive.set(isActive);
	}

	// ==================== ZEICHNUNGSLAYER MANAGEMENT ====================

	/**
	 * Entfernt alle Zeichnungslayer von der Map (bei Projekt-Wechsel)
	 */
	clearZeichnungslayer(): void {
		this.stopZeichnungslayerEdit();

		if (this.zeichnungenLinien) {
			this.zeichnungenLinien.eachLayer((l: any) => {
				if (l._clickBuffer) {
					l._clickBuffer.remove();
					l._clickBuffer = undefined;
				}
			});
			this.zeichnungenLinien.remove();
			this.zeichnungenLinien = undefined;
		}
		if (this.zeichnungenPolygone) {
			this.zeichnungenPolygone.remove();
			this.zeichnungenPolygone = undefined;
		}
		this.zeichnungen.clear();
	}

	/**
	 * Lädt alle Zeichnungslayer aus dem ProjectService
	 */
	async loadZeichnungslayer(): Promise<void> {
		this.stopZeichnungslayerEdit();
		this.clearZeichnungslayer();

		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		this.zeichnungslayerCache = layers ?? [];

		this.zeichnungenLinien = L.featureGroup([]);
		this.zeichnungenPolygone = L.featureGroup([]);

		if (layers && layers.length > 0 && this.map) {
			for (const layer of layers) {
				const linienPaneName = `zeichnung-${layer.id}-linien`;
				const polygonePaneName = `zeichnung-${layer.id}-polygone`;

				if (!this.map.getPane(linienPaneName)) {
					const pane = this.map.createPane(linienPaneName);
					pane.style.zIndex = '450';
				}
				if (!this.map.getPane(polygonePaneName)) {
					const pane = this.map.createPane(polygonePaneName);
					pane.style.zIndex = '440';
				}

				const featureGroup = L.featureGroup();
				const layerLineStyle = {
					color: layer.strokeColor ?? '#3388ff',
					weight: layer.strokeWidth ?? 3
				};
				const layerPolygonStyle = {
					color: layer.strokeColor ?? '#3388ff',
					weight: layer.strokeWidth ?? 3,
					fillColor: layer.fillColor ?? '#3388ff',
					fillOpacity: layer.fillOpacity ?? 0.2
				};

				if (layer.geojson?.features) {
					L.geoJSON(layer.geojson, {
						style: (feature) => {
							const geomType = feature?.geometry?.type;
							const isLine = geomType === 'LineString' || geomType === 'MultiLineString';
							const pane = isLine ? linienPaneName : polygonePaneName;

							const savedStyle = feature?.properties?.style;
							if (savedStyle) {
								return {
									color: savedStyle.color,
									weight: savedStyle.weight,
									fillColor: savedStyle.fillColor,
									fillOpacity: savedStyle.fillOpacity,
									pane: pane
								};
							}

							const defaultStyle = isLine ? layerLineStyle : layerPolygonStyle;
							return {
								color: defaultStyle.color,
								weight: defaultStyle.weight,
								fillColor: (defaultStyle as any).fillColor,
								fillOpacity: (defaultStyle as any).fillOpacity,
								pane: pane
							};
						},
						onEachFeature: (feature, l) => {
							this.createShapePopup(l as L.Path & { feature?: any });

							const geomType = feature.geometry?.type;
							(l as any)._geomType = geomType;
							(l as any)._zeichnungslayerId = layer.id;

							if (geomType === 'LineString' || geomType === 'MultiLineString') {
								(l as any)._needsClickBuffer = true;
							}
						}
					}).eachLayer(l => {
						featureGroup.addLayer(l);

						if (layer.visible) {
							const geomType = (l as any)._geomType;
							if (geomType === 'LineString' || geomType === 'MultiLineString') {
								this.zeichnungenLinien!.addLayer(l);
							} else if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
								this.zeichnungenPolygone!.addLayer(l);
							}
						}
					});
				}

				this.zeichnungen.set(layer.id, featureGroup);
			}
		}

		if (this.map) {
			this.bridge.addLayerToMap('Zeichnungen_Linien', this.zeichnungenLinien, true);
			this.bridge.addLayerToMap('Zeichnungen_Polygone', this.zeichnungenPolygone, true);

			this.zeichnungenLinien?.eachLayer((l: any) => {
				if (!l._clickBuffer) {
					this.addClickBufferToLayer(l as L.Polyline);
				}
			});

			this.layerOrderingService.onLayerOrderChanged();
		}
	}

	/**
	 * Speichert alle Zeichnungslayer in den ProjectService.
	 * Schützt gegen Datenverlust: Wenn alle in-memory FeatureGroups leer sind,
	 * aber gespeicherte Daten Features enthalten, wird NICHT überschrieben.
	 */
	private async saveZeichnungslayer(): Promise<void> {
		this.isSavingZeichnungslayer = true;

		try {
			const existing = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null ?? [];

			// Guard: Prüfe ob in-memory FeatureGroups überhaupt geladen sind
			if (this.zeichnungen.size === 0 && existing.some(def => def.geojson?.features?.length > 0)) {
				console.warn('[DrawingService] Speichern abgebrochen: Keine FeatureGroups geladen, aber gespeicherte Daten vorhanden.');
				return;
			}

			const updated = existing.map(def => {
				const fg = this.zeichnungen.get(def.id);
				if (!fg) return def;

				const newGeojson = fg.toGeoJSON() as GeoJSON.FeatureCollection;

				return {
					...def,
					geojson: newGeojson
				};
			});

			await this.ps.setProjectValue('zeichnungslayer', updated);
			this.ls.zeichnungslayerChanged.emit();
		} catch (error) {
			console.error('[DrawingService] Fehler beim Speichern der Zeichnungslayer:', error);
			this.msg.error('Zeichnungslayer konnten nicht gespeichert werden. Bitte Zeichnungen über "Kartendaten exportieren" sichern.', 'Speicherfehler');
		} finally {
			this.isSavingZeichnungslayer = false;
		}
	}

	/**
	 * Zoomt zur Ausdehnung eines Zeichnungslayers
	 */
	private zoomToZeichnungslayer(layerId: string): void {
		const featureGroup = this.zeichnungen.get(layerId);
		if (!featureGroup || !this.map) return;

		const bounds = featureGroup.getBounds();
		if (bounds.isValid()) {
			this.map.fitBounds(bounds, { padding: [20, 20] });
		}
	}

	/**
	 * Gibt die Pane-Namen für einen Zeichnungslayer zurück und erstellt die Panes falls nötig.
	 */
	getZeichnungslayerPanes(layerId: string): { linien: string; polygone: string } {
		const linienPaneName = `zeichnung-${layerId}-linien`;
		const polygonePaneName = `zeichnung-${layerId}-polygone`;

		if (this.map) {
			if (!this.map.getPane(linienPaneName)) {
				const pane = this.map.createPane(linienPaneName);
				pane.style.zIndex = '450';
			}
			if (!this.map.getPane(polygonePaneName)) {
				const pane = this.map.createPane(polygonePaneName);
				pane.style.zIndex = '440';
			}
		}

		return { linien: linienPaneName, polygone: polygonePaneName };
	}

	/**
	 * Setzt den Z-Index für einen einzelnen Zeichnungslayer anhand seiner ID.
	 */
	setZeichnungslayerByIdZIndex(layerId: string, zIndex: number): void {
		const panes = this.getZeichnungslayerPanes(layerId);

		const linienPane = this.map.getPane(panes.linien);
		const polygonePane = this.map.getPane(panes.polygone);

		if (linienPane) {
			linienPane.style.zIndex = String(zIndex);
		}
		if (polygonePane) {
			polygonePane.style.zIndex = String(zIndex - 1);
		}
	}

	/**
	 * Gibt den Style des aktiven Zeichnungslayers zurück (async Version)
	 */
	private async getActiveLayerStyle(): Promise<{ strokeColor: string; strokeWidth: number; fillColor: string; fillOpacity: number }> {
		const defaultStyle = {
			strokeColor: '#3388ff',
			strokeWidth: 3,
			fillColor: '#3388ff',
			fillOpacity: 0.2
		};

		if (!this.activeZeichnungslayerId) return defaultStyle;

		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		if (!layers) return defaultStyle;

		const activeLayer = layers.find(l => l.id === this.activeZeichnungslayerId);
		if (!activeLayer) return defaultStyle;

		return {
			strokeColor: activeLayer.strokeColor ?? defaultStyle.strokeColor,
			strokeWidth: activeLayer.strokeWidth ?? defaultStyle.strokeWidth,
			fillColor: activeLayer.fillColor ?? defaultStyle.fillColor,
			fillOpacity: activeLayer.fillOpacity ?? defaultStyle.fillOpacity
		};
	}

	/**
	 * Gibt den Style des aktiven Zeichnungslayers zurück (synchrone Version)
	 */
	private getActiveLayerStyleSync(): { strokeColor: string; strokeWidth: number; fillColor: string; fillOpacity: number } {
		const defaultStyle = {
			strokeColor: '#3388ff',
			strokeWidth: 3,
			fillColor: '#3388ff',
			fillOpacity: 0.2
		};

		if (!this.activeZeichnungslayerId) return defaultStyle;

		const activeLayer = this.zeichnungslayerCache.find(l => l.id === this.activeZeichnungslayerId);
		if (!activeLayer) return defaultStyle;

		return {
			strokeColor: activeLayer.strokeColor ?? defaultStyle.strokeColor,
			strokeWidth: activeLayer.strokeWidth ?? defaultStyle.strokeWidth,
			fillColor: activeLayer.fillColor ?? defaultStyle.fillColor,
			fillOpacity: activeLayer.fillOpacity ?? defaultStyle.fillOpacity
		};
	}

	/**
	 * Aktualisiert den Zwischenspeicher für Layer-Styles
	 */
	private async updateZeichnungslayerCache(): Promise<void> {
		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		this.zeichnungslayerCache = layers ?? [];
	}

	/**
	 * Toggled die Sichtbarkeit eines Zeichnungslayers auf der Karte
	 */
	private async toggleZeichnungslayerVisibility(layerId: string): Promise<void> {
		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		if (!layers) return;

		const featureGroup = this.zeichnungen.get(layerId);
		if (!featureGroup) return;

		const layerDef = layers.find(l => l.id === layerId);
		if (!layerDef) return;

		featureGroup.eachLayer((l: any) => {
			const geomType = l._geomType || l.feature?.geometry?.type;
			const isLine = geomType === 'LineString' || geomType === 'MultiLineString';
			const isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon';

			const targetGroup = isLine ? this.zeichnungenLinien : (isPolygon ? this.zeichnungenPolygone : null);
			if (!targetGroup) return;

			if (layerDef.visible) {
				if (!targetGroup.hasLayer(l)) {
					targetGroup.addLayer(l);
					if (isLine && !l._clickBuffer) {
						this.addClickBufferToLayer(l as L.Polyline);
					}
				}
			} else {
				if (targetGroup.hasLayer(l)) {
					targetGroup.removeLayer(l);
				}
			}
		});

		this.layerOrderingService.onLayerOrderChanged();
	}

	/**
	 * Parst eine Farbangabe (hex oder rgba) und gibt Farbe und Opacity zurück
	 */
	private parseColor(colorStr: string): { color: string; opacity: number } {
		const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
		if (rgbaMatch) {
			const r = parseInt(rgbaMatch[1]);
			const g = parseInt(rgbaMatch[2]);
			const b = parseInt(rgbaMatch[3]);
			const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1;
			const hex = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
			return { color: hex, opacity: a };
		}
		return { color: colorStr, opacity: 0.2 };
	}

	private addPopupToFeature(layer: L.FeatureGroup | any): void {
		if (!layer.feature) return;

		const content = document.createElement("textarea");
		content.className = "form-control";
		content.rows = 3;
		content.style.width = "200px";
		content.style.minWidth = "200px";
		content.placeholder = "Beschreibung eingeben...";

		content.addEventListener("keyup", () => {
			(layer.feature! as any).properties.desc = content.value;
		});

		layer.on("popupopen", () => {
			content.value = (layer.feature! as any).properties?.desc || '';
			if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) {
				content.focus();
			}
		});

		layer.on("popupclose", () => {
			this.saveZeichnungslayer();
		});

		layer.bindPopup(content, { minWidth: 220 });
	}

	/**
	 * Startet den Bearbeitungsmodus für einen Zeichnungslayer
	 */
	startZeichnungslayerEdit(layerId: string): void {
		this.stopZeichnungslayerEdit();

		const featureGroup = this.zeichnungen.get(layerId);
		if (!featureGroup || !this.map) return;

		this.activeZeichnungslayerId = layerId;

		featureGroup.eachLayer((l: any) => {
			const geomType = l._geomType || l.feature?.geometry?.type;
			const isLine = geomType === 'LineString' || geomType === 'MultiLineString';
			const isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon';

			const targetGroup = isLine ? this.zeichnungenLinien : (isPolygon ? this.zeichnungenPolygone : null);
			if (targetGroup && !targetGroup.hasLayer(l)) {
				targetGroup.addLayer(l);
			}
		});

		this.layerOrderingService.onLayerOrderChanged();
		this.initDrawLocalization();
		this.registerDrawEvents();

		if (!this.gpsUpdateTimer) {
			this.createGpsUpdateTimer();
		}
	}

	/**
	 * Beendet den Bearbeitungsmodus
	 */
	stopZeichnungslayerEdit(): void {
		if (!this.activeZeichnungslayerId) return;

		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();

		this.saveZeichnungslayer();

		this.gpsUpdateTimer?.destroy();
		this.gpsUpdateTimer = undefined;

		if (this.currentTrackPolyline) {
			this.currentTrackPolyline = undefined;
		}

		this.activeZeichnungslayerId = null;
	}

	/**
	 * Prüft ob ein Zeichnungslayer aktiv ist.
	 */
	private checkActiveZeichnungslayer(): string | null {
		let activeId = this.ls.activeDrawLayerId();

		if (!activeId) {
			const visibleLayers = this.zeichnungslayerCache.filter(l => l.visible !== false);

			if (visibleLayers.length === 0) {
				this.msg.info(
					'Bitte erstellen Sie zuerst einen Zeichnungslayer im Layermenü.',
					'Kein Zeichnungslayer vorhanden'
				);
				return null;
			}

			if (visibleLayers.length === 1) {
				activeId = visibleLayers[0].id;
			} else {
				const lastActiveId = this.ls.lastActiveDrawLayerId();
				if (lastActiveId && visibleLayers.some(l => l.id === lastActiveId)) {
					activeId = lastActiveId;
				} else {
					this.msg.info(
						'Bitte wählen Sie einen Zeichnungslayer im Layermenü (Bleistift-Symbol).',
						'Kein Zeichnungslayer ausgewählt'
					);
					return null;
				}
			}

			this.ls.activeDrawLayerId.set(activeId);
			this.ls.lastActiveDrawLayerId.set(activeId);
		}

		if (!this.zeichnungenLinien || !this.zeichnungenPolygone) {
			if (!this.zeichnungenLinien) {
				this.zeichnungenLinien = L.featureGroup([]);
				if (this.map) this.bridge.addLayerToMap('Zeichnungen_Linien', this.zeichnungenLinien, true);
			}
			if (!this.zeichnungenPolygone) {
				this.zeichnungenPolygone = L.featureGroup([]);
				if (this.map) this.bridge.addLayerToMap('Zeichnungen_Polygone', this.zeichnungenPolygone, true);
			}
		}

		this.getZeichnungslayerPanes(activeId);

		if (!this.zeichnungen.has(activeId)) {
			const featureGroup = L.featureGroup();
			this.zeichnungen.set(activeId, featureGroup);
		}

		this.activeZeichnungslayerId = activeId;
		this.registerDrawEvents();

		if (!this.gpsUpdateTimer) {
			this.createGpsUpdateTimer();
		}

		return activeId;
	}

	// ==================== DRAW EVENTS ====================

	private registerDrawEvents(): void {
		if (this.drawEventsRegistered || !this.map) return;

		this.map.on(L.Draw.Event.CREATED, this.onDrawCreated);
		this.map.on(L.Draw.Event.EDITED, this.onDrawEdited);
		this.map.on(L.Draw.Event.DELETED, this.onDrawDeleted);
		this.map.on(L.Draw.Event.DELETESTOP, this.onDrawDeleteStop);
		this.map.on(L.Draw.Event.EDITSTOP, this.onDrawEditStop);
		this.drawEventsRegistered = true;
	}

	private onDrawCreated = async (event: any) => {
		const featureGroup = this.zeichnungen.get(this.activeZeichnungslayerId!);
		if (!featureGroup || !this.map || !this.activeZeichnungslayerId) return;

		const originalLayer = event.layer;
		const isLine = originalLayer instanceof L.Polyline && !(originalLayer instanceof L.Polygon);
		const isPolygon = originalLayer instanceof L.Polygon;

		const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);
		const paneName = isLine ? panes.linien : panes.polygone;

		const layerStyle = await this.getActiveLayerStyle();
		const style: L.PathOptions = isLine
			? { color: layerStyle.strokeColor, weight: layerStyle.strokeWidth }
			: { color: layerStyle.strokeColor, weight: layerStyle.strokeWidth, fillColor: layerStyle.fillColor, fillOpacity: layerStyle.fillOpacity };

		let newLayer: L.Path;
		if (isPolygon) {
			newLayer = L.polygon(originalLayer.getLatLngs() as L.LatLng[][], { ...style, pane: paneName });
		} else {
			newLayer = L.polyline(originalLayer.getLatLngs() as L.LatLng[], { ...style, pane: paneName });
		}

		const feature = (newLayer as any).feature = (newLayer as any).feature || {};
		feature.type = feature.type || "Feature";
		const props = feature.properties = feature.properties || {};
		props.desc = '';
		props.style = {
			color: layerStyle.strokeColor,
			weight: layerStyle.strokeWidth,
			fillColor: layerStyle.fillColor,
			fillOpacity: layerStyle.fillOpacity
		};

		(newLayer as any)._geomType = isLine ? 'LineString' : (isPolygon ? 'Polygon' : null);

		this.addPopupToFeature(newLayer);
		featureGroup.addLayer(newLayer);

		const targetGroup = isLine ? this.zeichnungenLinien : (isPolygon ? this.zeichnungenPolygone : null);
		if (targetGroup) {
			targetGroup.addLayer(newLayer);
			if (!this.map.hasLayer(targetGroup)) {
				this.bridge.addLayerToMap(isLine ? 'Zeichnungen_Linien' : 'Zeichnungen_Polygone', targetGroup, true);
			}
		}

		this.layerOrderingService.onLayerOrderChanged();

		if (isLine) {
			this.addClickBufferToLayer(newLayer as L.Polyline);
		}

		this.saveZeichnungslayer();
	};

	private onDrawEdited = () => {
		this.saveZeichnungslayer();
	};

	private onDrawDeleted = (event: any) => {
		if (event.layers) {
			event.layers.eachLayer((layer: any) => {
				if (this.zeichnungenLinien?.hasLayer(layer)) {
					this.zeichnungenLinien.removeLayer(layer);
				}
				if (this.zeichnungenPolygone?.hasLayer(layer)) {
					this.zeichnungenPolygone.removeLayer(layer);
				}
				this.zeichnungen.forEach((fg) => {
					if (fg.hasLayer(layer)) {
						fg.removeLayer(layer);
					}
				});
				if ((layer as any)._clickBuffer) {
					(layer as any)._clickBuffer.remove();
				}
				if (this.map?.hasLayer(layer)) {
					this.map.removeLayer(layer);
				}
			});
		}
		this.saveZeichnungslayer();
	};

	private onDrawDeleteStop = () => {
		this.saveZeichnungslayer();
	};

	private onDrawEditStop = () => {
		this.saveZeichnungslayer();
	};

	private initDrawLocalization(): void {
		L.drawLocal.draw.toolbar.buttons.polygon = "Fläche zeichnen";
		L.drawLocal.draw.toolbar.actions = {
			title: "Zeichnen abbrechen",
			text: "Abbrechen"
		};
		L.drawLocal.draw.toolbar.finish = {
			title: "Zeichnen beenden",
			text: "Beenden"
		};
		L.drawLocal.draw.toolbar.undo = {
			title: "Letzten gezeichneten Punkt löschen",
			text: "Letzten Punkt löschen"
		};
		L.drawLocal.draw.handlers.polyline.error = "<strong>Fehler:</strong> Überschneidungen!";
		L.drawLocal.draw.handlers.polygon.tooltip = {
			start: "",
			cont: "",
			end: ""
		};
		L.drawLocal.edit = {
			toolbar: {
				actions: {
					save: {
						title: "Änderungen speichern",
						text: "Speichern"
					},
					cancel: {
						title: "Bearbeiten abbrechen, Änderungen werden verworfen",
						text: "Abbrechen"
					},
					clearAll: {
						title: "Alle Zeichnungen löschen",
						text: "Alle löschen"
					}
				},
				buttons: {
					edit: "Zeichnungen bearbeiten",
					editDisabled: "Keine Zeichnungen zum Bearbeiten",
					remove: "Zeichnungen löschen",
					removeDisabled: "Keine Zeichnungen zum Löschen"
				}
			},
			handlers: {
				edit: {
					tooltip: {
						text: "Zum Bearbeiten Marker ziehen",
						subtext: ""
					}
				},
				remove: {
					tooltip: {
						text: "Zum Löschen Feature anklicken"
					}
				}
			}
		};
	}

	// ==================== GPS UPDATE TIMER ====================

	private createGpsUpdateTimer(): void {
		let previous: LatLng | undefined;
		const calcCrow = (ll1: LatLng, ll2: LatLng) => {
			const toRad = (angle: number) => angle * Math.PI / 180;
			const R = 6371;
			const dLat = toRad(ll2.lat - ll1.lat);
			const dLon = toRad(ll2.lng - ll1.lng);

			const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(toRad(ll1.lat)) * Math.cos(toRad(ll2.lat));
			const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
			const d = R * c;
			return d;
		};
		const isValidLatLngLocal = (ll: LatLng): boolean => {
			return ll && isFinite(ll.lat) && isFinite(ll.lng) && !isNaN(ll.lat) && !isNaN(ll.lng);
		};

		let wasPolygonMode = false;

		this.gpsUpdateTimer = new GpsUpdateTimer({
			updateCallback: () => {
				if (!this.gpsTrackingPolygonEnabled && !this.gpsTrackingLineEnabled) {
					return;
				}

				const current = this.bridge.getCurrentPositionLatLng();
				if (!isValidLatLngLocal(current)) {
					return;
				}

				if (!previous) {
					previous = current;
					return;
				}
				const dist = calcCrow(previous, current);
				if (dist > 0) {
					this.map.panTo(current);
					if (this.currentTrackPolyline) {
						this.currentTrackPolyline.addLatLng(current);
					}
					else {
						const layerStyle = this.getActiveLayerStyleSync();
						const panes = this.activeZeichnungslayerId
							? this.getZeichnungslayerPanes(this.activeZeichnungslayerId)
							: null;
						this.currentTrackPolyline = L.polyline([previous, current], {
							color: layerStyle.strokeColor,
							weight: layerStyle.strokeWidth,
							pane: panes?.linien
						});
						(this.currentTrackPolyline as any).feature = {
							type: 'Feature',
							properties: {
								desc: '',
								style: {
									color: layerStyle.strokeColor,
									weight: layerStyle.strokeWidth
								}
							}
						};
						(this.currentTrackPolyline as any)._geomType = 'LineString';
						this.createShapePopup(this.currentTrackPolyline);
						this.currentTrackPolyline.addTo(this.map);
						const activeFeatureGroup = this.activeZeichnungslayerId
							? this.zeichnungen.get(this.activeZeichnungslayerId)
							: null;
						if (activeFeatureGroup) {
							activeFeatureGroup.addLayer(this.currentTrackPolyline);
						}
					}
					previous = current;
				}
			},
			interval: 10000
		});

		this.gpsUpdateTimer.toggled.subscribe((toggled: boolean) => {
			if (!toggled) {
				if (this.currentTrackPolyline && this.activeZeichnungslayerId) {
					const latLngs = this.currentTrackPolyline.getLatLngs() as L.LatLng[];

					if (wasPolygonMode && latLngs.length >= 3) {
						const layerStyle = this.getActiveLayerStyleSync();
						const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);

						this.currentTrackPolyline.remove();

						const activeFeatureGroup = this.zeichnungen.get(this.activeZeichnungslayerId);
						if (activeFeatureGroup) {
							activeFeatureGroup.removeLayer(this.currentTrackPolyline);
						}

						const polygon = L.polygon(latLngs, {
							color: layerStyle.strokeColor,
							weight: layerStyle.strokeWidth,
							fillColor: layerStyle.fillColor,
							fillOpacity: layerStyle.fillOpacity,
							pane: panes.polygone
						});

						(polygon as any).feature = {
							type: 'Feature',
							properties: {
								desc: '',
								style: {
									color: layerStyle.strokeColor,
									weight: layerStyle.strokeWidth,
									fillColor: layerStyle.fillColor,
									fillOpacity: layerStyle.fillOpacity
								}
							}
						};
						(polygon as any)._geomType = 'Polygon';

						this.createShapePopup(polygon);

						polygon.addTo(this.map);
						if (activeFeatureGroup) {
							activeFeatureGroup.addLayer(polygon);
						}
					}

					this.currentTrackPolyline = undefined;
					this.saveZeichnungslayer();
				}

				wasPolygonMode = false;
				this.gpsTrackingPolygonEnabled = false;
				this.gpsTrackingLineEnabled = false;
				previous = undefined;
			} else {
				wasPolygonMode = this.gpsTrackingPolygonEnabled;

				const pos = this.bridge.getCurrentPositionLatLng();
				if (isValidLatLngLocal(pos)) {
					previous = pos;
					this.map.panTo(previous);
				} else {
					previous = undefined;
				}
			}
		});
	}

	// ==================== NEW POINT CREATION ====================

	canCreateNewPoint(): boolean {
		return this.aus.canI('add', 'points');
	}

	showDrawControls(): boolean {
		return true;
	}

	onNewPointAtLocation(): void {
		this.ns.command(Command.addPoint).emit();
	}

	onNewPointOnMap(): void {
		this.startNewPointOnMapMode();
	}

	private startNewPointOnMapMode(): void {
		if (!this.map) return;

		if (!this.canCreateNewPoint()) {
			return;
		}

		this.onToolActivated?.();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.newPointOnMapActive = true;
		L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');

		this.setZeichnungslayerInteractive(false);

		this.newPointOnMapClickHandler = (e: any) => {
			this.ns.command(Command.addPoint).emit(e.latlng);
		};
		this.map.on('click', this.newPointOnMapClickHandler);
	}

	endNewPointOnMapMode(): void {
		if (!this.newPointOnMapActive) return;

		this.newPointOnMapActive = false;
		if (this.map) {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-crosshair');
			if (this.newPointOnMapClickHandler) {
				this.map.off('click', this.newPointOnMapClickHandler);
				this.newPointOnMapClickHandler = undefined;
			}
		}

		this.setZeichnungslayerInteractive(true);
	}

	private setZeichnungslayerInteractive(interactive: boolean): void {
		if (!this.map) return;

		const handleLayer = (layer: any) => {
			if (interactive) {
				layer.off('click', this.zeichnungClickInterceptor);
				layer._clickBuffer?.off('click', this.zeichnungClickInterceptor);
			} else {
				layer.on('click', this.zeichnungClickInterceptor);
				layer._clickBuffer?.on('click', this.zeichnungClickInterceptor);
			}
		};

		this.zeichnungenLinien?.eachLayer(handleLayer);
		this.zeichnungenPolygone?.eachLayer(handleLayer);

		if (!interactive) {
			this.map.closePopup();
		}
	}

	// ==================== POLYGON DRAWING ====================

	onToggleDraw(): void {
		if (this.drawEnabled) {
			this.drawEnabled = false;
			this.drawPolygonPoints = 0;
			this.map.off('click', this.onDrawPolygonClick, this);
			this.drawHandler?.disable();
			this.drawHandler = undefined;
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'draw';
			return;
		}

		this.activateDrawMode();
	}

	private activateDrawMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.drawEnabled = true;
		this.drawPolygonPoints = 0;
		this.updateDrawToolActiveState();

		if (!this.activeZeichnungslayerId) return;
		const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);

		const layerStyle = this.getActiveLayerStyleSync();
		const style: L.PathOptions = {
			color: layerStyle.strokeColor,
			weight: layerStyle.strokeWidth,
			fillColor: layerStyle.fillColor,
			fillOpacity: layerStyle.fillOpacity
		};

		this.drawHandler = new (L.Draw as any).Polygon(this.map, {
			allowIntersection: false,
			showArea: false,
			showLength: false,
			shapeOptions: { ...style, pane: panes.polygone }
		});
		this.drawHandler?.enable();

		this.map.on('click', this.onDrawPolygonClick, this);
	}

	private onDrawPolygonClick(): void {
		if (this.drawHandler?._markers) {
			this.drawPolygonPoints = this.drawHandler._markers.length;
		}
	}

	onFinishDrawPolygon(): void {
		if (this.drawHandler?._markers && this.drawHandler._markers.length >= 3) {
			this.drawHandler.completeShape?.();
			this.drawPolygonPoints = 0;
			this.hasDrawnPolygon = true;
		}
	}

	private endDrawMode(): void {
		if (this.drawHandler) {
			this.map.off('click', this.onDrawPolygonClick, this);
			this.drawHandler?.disable();
			this.drawHandler = undefined;
			this.drawEnabled = false;
			this.drawPolygonPoints = 0;
			this.updateDrawToolActiveState();
		}
	}

	// ==================== FREEHAND POLYGON ====================

	async onToggleFreehand(): Promise<void> {
		if (this.freehandEnabled) {
			this.endFreehandMode();
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'freehand';
			return;
		}

		this.activateFreehandMode();
	}

	private activateFreehandMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.freehandEnabled = true;
		this.updateDrawToolActiveState();

		L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
		this.map.dragging.disable();

		this.map.on('mousedown', this.onFreehandMouseDown, this);
		this.map.on('mousemove', this.onFreehandMouseMove, this);
		this.map.on('mouseup', this.onFreehandMouseUp, this);

		const container = this.map.getContainer();
		container.addEventListener('touchstart', this.onFreehandTouchStart, { passive: false });
		container.addEventListener('touchmove', this.onFreehandTouchMove, { passive: false });
		container.addEventListener('touchend', this.onFreehandTouchEnd, { passive: false });
		container.addEventListener('contextmenu', this.onPreventContextMenu, { passive: false });
	}

	private endFreehandMode(): void {
		if (!this.freehandEnabled) return;

		this.freehandEnabled = false;
		this.freehandDrawing = false;
		this.freehandPoints = [];
		this.lastFreehandPoint = undefined;
		this.freehandPolyline?.remove();
		this.freehandPolyline = undefined;
		this.updateDrawToolActiveState();

		if (this.map) {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.dragging.enable();

			this.map.off('mousedown', this.onFreehandMouseDown);
			this.map.off('mousemove', this.onFreehandMouseMove);
			this.map.off('mouseup', this.onFreehandMouseUp);

			const container = this.map.getContainer();
			container.removeEventListener('touchstart', this.onFreehandTouchStart);
			container.removeEventListener('touchmove', this.onFreehandTouchMove);
			container.removeEventListener('touchend', this.onFreehandTouchEnd);
			container.removeEventListener('contextmenu', this.onPreventContextMenu);
		}
	}

	private onPreventContextMenu = (e: Event): void => {
		e.preventDefault();
	}

	private onFreehandMouseDown(e: L.LeafletMouseEvent): void {
		if (!this.freehandEnabled) return;
		this.startFreehandDrawing(e.latlng);
	}

	private onFreehandMouseMove(e: L.LeafletMouseEvent): void {
		if (!this.freehandEnabled || !this.freehandDrawing) return;
		this.continueFreehandDrawing(e.latlng, e.containerPoint);
	}

	private onFreehandMouseUp(e: L.LeafletMouseEvent): void {
		if (!this.freehandEnabled || !this.freehandDrawing) return;
		this.finishFreehandDrawing();
	}

	private onFreehandTouchStart = (e: TouchEvent): void => {
		if (!this.freehandEnabled) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.startFreehandDrawing(latlng);
	}

	private onFreehandTouchMove = (e: TouchEvent): void => {
		if (!this.freehandEnabled || !this.freehandDrawing) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.continueFreehandDrawing(latlng, containerPoint);
	}

	private onFreehandTouchEnd = (e: TouchEvent): void => {
		if (!this.freehandEnabled || !this.freehandDrawing) return;

		e.preventDefault();
		this.finishFreehandDrawing();
	}

	private startFreehandDrawing(latlng: L.LatLng): void {
		this.freehandDrawing = true;
		this.freehandPoints = [latlng];
		this.lastFreehandPoint = latlng;

		if (!this.activeZeichnungslayerId) return;
		const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);

		const layerStyle = this.getActiveLayerStyleSync();
		const style: L.PathOptions = {
			color: layerStyle.strokeColor,
			weight: layerStyle.strokeWidth,
			fillColor: layerStyle.fillColor,
			fillOpacity: layerStyle.fillOpacity
		};
		this.freehandPolyline = L.polyline([latlng], { ...style, pane: panes.polygone }).addTo(this.map);
	}

	private continueFreehandDrawing(latlng: L.LatLng, containerPoint: L.Point): void {
		if (!this.lastFreehandPoint || !this.freehandPolyline) return;

		const lastContainerPoint = this.map.latLngToContainerPoint(this.lastFreehandPoint);
		const distance = containerPoint.distanceTo(lastContainerPoint);

		if (distance >= this.FREEHAND_MIN_DISTANCE) {
			this.freehandPoints.push(latlng);
			this.freehandPolyline.addLatLng(latlng);
			this.lastFreehandPoint = latlng;
		}
	}

	private async finishFreehandDrawing(): Promise<void> {
		this.freehandDrawing = false;

		if (this.freehandPoints.length >= 3) {
			if (!this.checkActiveZeichnungslayer()) {
				this.freehandPolyline?.remove();
				this.freehandPolyline = undefined;
				this.freehandPoints = [];
				this.lastFreehandPoint = undefined;
				return;
			}
			const featureGroup = this.zeichnungen.get(this.activeZeichnungslayerId!);
			if (!featureGroup) {
				this.freehandPolyline?.remove();
				this.freehandPolyline = undefined;
				this.freehandPoints = [];
				this.lastFreehandPoint = undefined;
				return;
			}

			const closedPoints = [...this.freehandPoints, this.freehandPoints[0]];
			const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId!);

			const layerStyle = this.getActiveLayerStyleSync();
			const style: L.PathOptions = {
				color: layerStyle.strokeColor,
				weight: layerStyle.strokeWidth,
				fillColor: layerStyle.fillColor,
				fillOpacity: layerStyle.fillOpacity
			};
			const polygon = L.polygon(closedPoints, { ...style, pane: panes.polygone });

			const feature = (polygon as any).feature = (polygon as any).feature || {};
			feature.type = feature.type || "Feature";
			const props = feature.properties = feature.properties || {};
			props.desc = '';
			props.style = {
				color: layerStyle.strokeColor,
				weight: layerStyle.strokeWidth,
				fillColor: layerStyle.fillColor,
				fillOpacity: layerStyle.fillOpacity
			};
			(polygon as any)._geomType = 'Polygon';

			this.createShapePopup(polygon);

			featureGroup.addLayer(polygon);
			this.hasDrawnPolygon = true;

			if (this.zeichnungenPolygone) {
				this.zeichnungenPolygone.addLayer(polygon);
				if (this.map && !this.map.hasLayer(this.zeichnungenPolygone)) {
					this.bridge.addLayerToMap('Zeichnungen_Polygone', this.zeichnungenPolygone, true);
				}
			}

			this.layerOrderingService.onLayerOrderChanged();

			this.saveZeichnungslayer();

			this.freehandPolyline?.remove();
			this.freehandPolyline = undefined;
			this.freehandPoints = [];
			this.lastFreehandPoint = undefined;
		} else {
			this.freehandPolyline?.remove();
			this.freehandPolyline = undefined;
			this.freehandPoints = [];
			this.lastFreehandPoint = undefined;
		}
	}

	// ==================== EDIT / DELETE ====================

	async onEditDraw(): Promise<void> {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		if (this.editHandler) {
			this.endEditMode();
		} else {
			if (!this.checkActiveZeichnungslayer()) return;

			const featureGroup = this.zeichnungen.get(this.activeZeichnungslayerId!);
			if (!featureGroup) return;

			this.editHandler = new (L.EditToolbar as any).Edit(this.map, {
				featureGroup: featureGroup
			});
			this.editHandler?.enable();
			this.editEnabled = true;
			this.updateDrawToolActiveState();
		}
	}

	async onDeleteDraw(): Promise<void> {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		if (this.deleteEnabled) {
			this.endDeleteMode();
		} else {
			if (!this.checkActiveZeichnungslayer()) return;

			this.deleteEnabled = true;
			this.updateDrawToolActiveState();
			L.DomUtil.addClass(this.map.getContainer(), 'leaflet-delete-mode');

			this.map?.dragging.disable();

			this.deleteClickHandler = (e: L.LeafletMouseEvent) => {
				const clickedLayer = e.target;
				this.deleteLayerDirectly(clickedLayer);
			};

			this.deleteMouseoverHandler = (e: L.LeafletMouseEvent) => {
				if (this.deleteMouseDown) {
					const layer = e.target;
					this.deleteLayerDirectly(layer);
				}
			};

			this.deleteMoveHandler = (e: L.LeafletMouseEvent) => {
				if (this.deleteMouseDown && this.map) {
					this.deleteTrailPoints.push(e.latlng);
					if (!this.deleteTrail) {
						this.deleteTrail = L.polyline(this.deleteTrailPoints, {
							color: 'red',
							weight: 6,
							opacity: 0.8,
							dashArray: '10, 5',
							pane: 'tooltipPane'
						}).addTo(this.map);
					} else {
						this.deleteTrail.setLatLngs(this.deleteTrailPoints);
					}
				}
			};

			this.deleteMousedownHandler = (e: L.LeafletMouseEvent) => {
				this.deleteMouseDown = true;
				this.deleteTrailPoints = [e.latlng];
			};
			this.deleteMouseupHandler = () => {
				this.deleteMouseDown = false;
				if (this.deleteTrail) {
					const trail = this.deleteTrail;
					setTimeout(() => {
						trail.remove();
					}, 300);
					this.deleteTrail = null;
					this.deleteTrailPoints = [];
				}
			};
			this.deleteMouseleaveHandler = () => { this.deleteMouseDown = false; };
			this.map?.on('mousedown', this.deleteMousedownHandler);
			this.map?.on('mouseup', this.deleteMouseupHandler);
			this.map?.on('mouseleave', this.deleteMouseleaveHandler);
			this.map?.on('mousemove', this.deleteMoveHandler);

			const container = this.map?.getContainer();
			if (container) {
				this.deleteTouchStartHandler = (e: TouchEvent) => {
					if (e.touches.length !== 1) return;
					this.deleteMouseDown = true;
					const touch = e.touches[0];
					const latlng = this.map.containerPointToLatLng(L.point(touch.clientX - container.getBoundingClientRect().left, touch.clientY - container.getBoundingClientRect().top));
					this.deleteTrailPoints = [latlng];
				};
				this.deleteTouchMoveHandler = (e: TouchEvent) => {
					if (!this.deleteMouseDown || e.touches.length !== 1) return;
					e.preventDefault();
					const touch = e.touches[0];
					const rect = container.getBoundingClientRect();
					const latlng = this.map.containerPointToLatLng(L.point(touch.clientX - rect.left, touch.clientY - rect.top));
					this.deleteTrailPoints.push(latlng);
					if (!this.deleteTrail) {
						this.deleteTrail = L.polyline(this.deleteTrailPoints, {
							color: 'red',
							weight: 6,
							opacity: 0.8,
							dashArray: '10, 5',
							pane: 'tooltipPane'
						}).addTo(this.map);
					} else {
						this.deleteTrail.setLatLngs(this.deleteTrailPoints);
					}
					this.checkTouchOverLayers(latlng);
				};
				this.deleteTouchEndHandler = () => {
					this.deleteMouseDown = false;
					if (this.deleteTrail) {
						const trail = this.deleteTrail;
						setTimeout(() => trail.remove(), 300);
						this.deleteTrail = null;
						this.deleteTrailPoints = [];
					}
				};
				container.addEventListener('touchstart', this.deleteTouchStartHandler, { passive: false });
				container.addEventListener('touchmove', this.deleteTouchMoveHandler, { passive: false });
				container.addEventListener('touchend', this.deleteTouchEndHandler);
				container.addEventListener('touchcancel', this.deleteTouchEndHandler);
			}

			const activeLayerId = this.ls.activeDrawLayerId();
			this.deleteActiveLayerId = activeLayerId;
			const activeFeatureGroup = activeLayerId ? this.zeichnungen.get(activeLayerId) : null;

			if (activeFeatureGroup) {
				activeFeatureGroup.eachLayer((l: any) => {
					l.on('click', this.deleteClickHandler);
					l.on('mouseover', this.deleteMouseoverHandler);
					if (l.setStyle) l.setStyle({ cursor: 'pointer' });
					const element = l.getElement?.();
					if (element) element.classList.add('zeichnung-deletable');
					if (l._clickBuffer) {
						l._clickBuffer.on('click', () => this.deleteLayerDirectly(l));
						l._clickBuffer.on('mouseover', () => {
							if (this.deleteMouseDown) this.deleteLayerDirectly(l);
						});
						const bufferElement = l._clickBuffer.getElement?.();
						if (bufferElement) bufferElement.classList.add('zeichnung-deletable');
					}
				});
			}
		}
	}

	private checkTouchOverLayers(latlng: L.LatLng): void {
		if (!this.deleteEnabled) return;
		const tolerance = 15;

		const activeLayerId = this.ls.activeDrawLayerId();
		const activeFeatureGroup = activeLayerId ? this.zeichnungen.get(activeLayerId) : null;

		activeFeatureGroup?.eachLayer((layer: any) => {
			if (this.isPointNearLayer(latlng, layer, tolerance)) {
				this.deleteLayerDirectly(layer);
			}
		});
	}

	private isPointNearLayer(latlng: L.LatLng, layer: any, tolerancePx: number): boolean {
		if (!this.map) return false;
		const point = this.map.latLngToContainerPoint(latlng);

		if (layer.getLatLngs) {
			const latlngs = layer.getLatLngs();
			const flatLatLngs = Array.isArray(latlngs[0]) ? latlngs.flat() : latlngs;
			for (let i = 0; i < flatLatLngs.length - 1; i++) {
				const p1 = this.map.latLngToContainerPoint(flatLatLngs[i]);
				const p2 = this.map.latLngToContainerPoint(flatLatLngs[i + 1]);
				const dist = this.pointToSegmentDistance(point, p1, p2);
				if (dist < tolerancePx) return true;
			}
		}
		return false;
	}

	private pointToSegmentDistance(p: L.Point, p1: L.Point, p2: L.Point): number {
		const dx = p2.x - p1.x;
		const dy = p2.y - p1.y;
		const lengthSq = dx * dx + dy * dy;
		if (lengthSq === 0) return p.distanceTo(p1);
		let t = ((p.x - p1.x) * dx + (p.y - p1.y) * dy) / lengthSq;
		t = Math.max(0, Math.min(1, t));
		const proj = L.point(p1.x + t * dx, p1.y + t * dy);
		return p.distanceTo(proj);
	}

	private deleteLayerDirectly(layer: any): void {
		if (!layer || !this.deleteEnabled) return;

		const activeLayerId = this.ls.activeDrawLayerId();
		const activeFeatureGroup = activeLayerId ? this.zeichnungen.get(activeLayerId) : null;
		if (!activeFeatureGroup?.hasLayer(layer)) return;

		layer.closePopup?.();

		const isLine = this.zeichnungenLinien?.hasLayer(layer) ?? false;
		const featureGroupId = activeLayerId;

		this.deletedLayersStack.push({ layer, isLine, featureGroupId });

		if (layer._clickBuffer) {
			layer._clickBuffer.remove();
		}

		if (this.zeichnungenLinien?.hasLayer(layer)) {
			this.zeichnungenLinien.removeLayer(layer);
		}
		if (this.zeichnungenPolygone?.hasLayer(layer)) {
			this.zeichnungenPolygone.removeLayer(layer);
		}

		this.zeichnungen.forEach((fg) => {
			if (fg.hasLayer(layer)) {
				fg.removeLayer(layer);
			}
		});

		if (this.map?.hasLayer(layer)) {
			this.map.removeLayer(layer);
		}
	}

	private endEditMode(): void {
		if (this.editHandler) {
			this.editHandler?.save();
			this.editHandler?.disable();
			this.editHandler = undefined;
			this.editEnabled = false;
			this.updateDrawToolActiveState();
			this.saveDrawLayer();
		}
	}

	endDeleteMode(confirmed = false): void {
		if (this.deleteHandler) {
			this.deleteHandler?.save();
			this.deleteHandler?.disable();
			this.deleteHandler = undefined;
		}

		if (this.deleteClickHandler && this.deleteActiveLayerId) {
			const activeFeatureGroup = this.zeichnungen.get(this.deleteActiveLayerId);
			activeFeatureGroup?.eachLayer((l: any) => {
				l.off('click', this.deleteClickHandler);
				l.off('mouseover', this.deleteMouseoverHandler);
				const element = l.getElement?.();
				if (element) element.classList.remove('zeichnung-deletable');
				if (l._clickBuffer) {
					l._clickBuffer.off('click');
					l._clickBuffer.off('mouseover');
					const bufferElement = l._clickBuffer.getElement?.();
					if (bufferElement) bufferElement.classList.remove('zeichnung-deletable');
				}
			});
			this.deleteClickHandler = null;
			this.deleteMouseoverHandler = null;
		}
		this.deleteActiveLayerId = null;

		if (this.deleteMousedownHandler) {
			this.map?.off('mousedown', this.deleteMousedownHandler);
			this.deleteMousedownHandler = null;
		}
		if (this.deleteMouseupHandler) {
			this.map?.off('mouseup', this.deleteMouseupHandler);
			this.deleteMouseupHandler = null;
		}
		if (this.deleteMouseleaveHandler) {
			this.map?.off('mouseleave', this.deleteMouseleaveHandler);
			this.deleteMouseleaveHandler = null;
		}
		if (this.deleteMoveHandler) {
			this.map?.off('mousemove', this.deleteMoveHandler);
			this.deleteMoveHandler = null;
		}

		const container = this.map?.getContainer();
		if (container) {
			if (this.deleteTouchStartHandler) {
				container.removeEventListener('touchstart', this.deleteTouchStartHandler);
				this.deleteTouchStartHandler = null;
			}
			if (this.deleteTouchMoveHandler) {
				container.removeEventListener('touchmove', this.deleteTouchMoveHandler);
				this.deleteTouchMoveHandler = null;
			}
			if (this.deleteTouchEndHandler) {
				container.removeEventListener('touchend', this.deleteTouchEndHandler);
				container.removeEventListener('touchcancel', this.deleteTouchEndHandler);
				this.deleteTouchEndHandler = null;
			}
		}
		this.deleteMouseDown = false;

		if (this.deleteTrail) {
			this.deleteTrail.remove();
			this.deleteTrail = null;
			this.deleteTrailPoints = [];
		}

		this.map?.dragging.enable();

		if (!confirmed && this.deletedLayersStack.length > 0) {
			for (const { layer, isLine, featureGroupId } of this.deletedLayersStack) {
				if (isLine) {
					this.zeichnungenLinien?.addLayer(layer);
					if (layer._clickBuffer) {
						layer._clickBuffer.addTo(this.map);
					} else {
						this.addClickBufferToLayer(layer);
					}
				} else {
					this.zeichnungenPolygone?.addLayer(layer);
				}

				if (featureGroupId) {
					const fg = this.zeichnungen.get(featureGroupId);
					fg?.addLayer(layer);
				}
			}
		} else if (confirmed && this.deletedLayersStack.length > 0) {
			this.saveZeichnungslayer();
		}

		this.deletedLayersStack = [];
		this.deleteEnabled = false;
		this.updateDrawToolActiveState();
		if (this.map) {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-delete-mode');
		}
	}

	onConfirmEdit(): void {
		this.endEditMode();
		if (this.deleteEnabled) {
			this.endDeleteMode(true);
		}
	}

	onCancelEdit(): void {
		if (this.editHandler) {
			this.editHandler?.revertLayers();
			this.editHandler?.disable();
			this.editHandler = undefined;
			this.editEnabled = false;
		}
		if (this.deleteEnabled) {
			this.endDeleteMode(false);
		} else if (this.deleteHandler) {
			this.deleteHandler?.revertLayers();
			this.deleteHandler?.disable();
			this.deleteHandler = undefined;
		}
	}

	private saveDrawLayer(): void {
		this.saveZeichnungslayer();
	}

	// ==================== SHAPE POPUP / STYLE ====================

	private saveStyleToFeature(layer: L.Path & { feature?: any }): void {
		if (!layer.feature) return;
		if (!layer.feature.properties) layer.feature.properties = {};
		layer.feature.properties.style = {
			color: layer.options.color,
			weight: layer.options.weight,
			fillColor: layer.options.fillColor,
			fillOpacity: layer.options.fillOpacity
		};
	}

	createShapePopup(layer: L.Path & { feature?: any }): void {
		if (!layer.feature) return;
		const pathLayer = layer;
		const isPolygon = layer instanceof L.Polygon;

		const container = document.createElement("div");
		container.className = 'shape-popup-container';

		const textarea = document.createElement("textarea");
		textarea.className = 'shape-description-input';
		textarea.placeholder = 'Beschreibung...';
		textarea.rows = 2;
		textarea.addEventListener("keyup", () => {
			layer.feature.properties.desc = textarea.value;
		});
		container.appendChild(textarea);

		const styleSection = document.createElement("div");
		styleSection.className = 'shape-style-section';

		const strokeLabel = document.createElement("span");
		strokeLabel.className = 'shape-style-label';
		strokeLabel.textContent = 'Linienfarbe';
		styleSection.appendChild(strokeLabel);

		const strokeRow = document.createElement("div");
		strokeRow.className = 'shape-color-row';
		this.drawStyleService.colorPalette.forEach(color => {
			const swatch = document.createElement("div");
			swatch.className = 'shape-color-swatch';
			swatch.style.backgroundColor = color;
			if (pathLayer.options.color === color) swatch.classList.add('selected');
			swatch.onclick = () => {
				strokeRow.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
				swatch.classList.add('selected');
				pathLayer.setStyle({ color });
				this.saveStyleToFeature(pathLayer);
			};
			strokeRow.appendChild(swatch);
		});
		styleSection.appendChild(strokeRow);

		const widthLabel = document.createElement("span");
		widthLabel.className = 'shape-style-label';
		widthLabel.textContent = 'Linienstärke';
		styleSection.appendChild(widthLabel);

		const widthRow = document.createElement("div");
		widthRow.className = 'shape-width-row';
		this.drawStyleService.strokeWidths.forEach(width => {
			const btn = document.createElement("button");
			btn.className = 'shape-width-btn';
			btn.textContent = `${width}`;
			if (pathLayer.options.weight === width) btn.classList.add('selected');
			btn.onclick = () => {
				widthRow.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
				btn.classList.add('selected');
				pathLayer.setStyle({ weight: width });
				this.saveStyleToFeature(pathLayer);
			};
			widthRow.appendChild(btn);
		});
		styleSection.appendChild(widthRow);

		if (isPolygon) {
			const fillLabel = document.createElement("span");
			fillLabel.className = 'shape-style-label';
			fillLabel.textContent = 'Flächenfarbe';
			styleSection.appendChild(fillLabel);

			const fillRow = document.createElement("div");
			fillRow.className = 'shape-color-row';
			this.drawStyleService.colorPalette.forEach(color => {
				const swatch = document.createElement("div");
				swatch.className = 'shape-color-swatch';
				swatch.style.backgroundColor = color;
				if (pathLayer.options.fillColor === color) swatch.classList.add('selected');
				swatch.onclick = () => {
					fillRow.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
					swatch.classList.add('selected');
					pathLayer.setStyle({ fillColor: color });
					this.saveStyleToFeature(pathLayer);
				};
				fillRow.appendChild(swatch);
			});
			styleSection.appendChild(fillRow);

			const opacityLabel = document.createElement("span");
			opacityLabel.className = 'shape-style-label';
			opacityLabel.textContent = 'Flächendeckung';
			styleSection.appendChild(opacityLabel);

			const opacityRow = document.createElement("div");
			opacityRow.className = 'shape-opacity-row';
			this.drawStyleService.fillOpacities.forEach(opacity => {
				const btn = document.createElement("button");
				btn.className = 'shape-opacity-btn';
				btn.style.backgroundColor = pathLayer.options.fillColor as string || '#3388ff';
				btn.style.opacity = String(opacity || 0.05);
				if (pathLayer.options.fillOpacity === opacity) btn.classList.add('selected');
				btn.title = `${Math.round(opacity * 100)}%`;
				btn.onclick = () => {
					opacityRow.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
					btn.classList.add('selected');
					pathLayer.setStyle({ fillOpacity: opacity });
					this.saveStyleToFeature(pathLayer);
				};
				opacityRow.appendChild(btn);
			});
			styleSection.appendChild(opacityRow);
		}

		container.appendChild(styleSection);

		const buttonRow = document.createElement("div");
		buttonRow.className = 'shape-button-row';

		const deleteBtn = document.createElement("button");
		deleteBtn.className = 'shape-delete-btn';
		deleteBtn.innerHTML = '<i class="bi bi-trash"></i>';
		deleteBtn.title = 'Löschen';
		deleteBtn.onclick = () => {
			layer.closePopup();
			const clickBuffer = (layer as any)._clickBuffer;
			if (clickBuffer) {
				clickBuffer.remove();
			}
			if (this.zeichnungenLinien?.hasLayer(layer)) {
				this.zeichnungenLinien.removeLayer(layer);
			}
			if (this.zeichnungenPolygone?.hasLayer(layer)) {
				this.zeichnungenPolygone.removeLayer(layer);
			}
			this.zeichnungen.forEach((fg) => {
				if (fg.hasLayer(layer)) {
					fg.removeLayer(layer);
				}
			});
			if (this.map?.hasLayer(layer)) {
				this.map.removeLayer(layer);
			}
			this.saveZeichnungslayer();
		};
		buttonRow.appendChild(deleteBtn);

		const okBtn = document.createElement("button");
		okBtn.className = 'shape-ok-btn';
		okBtn.innerHTML = '<i class="bi bi-check-lg"></i>';
		okBtn.title = 'OK';
		okBtn.onclick = () => {
			layer.closePopup();
		};
		buttonRow.appendChild(okBtn);

		container.appendChild(buttonRow);

		layer.on("popupopen", () => {
			textarea.value = layer.feature?.properties?.desc || '';
			if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) {
				textarea.focus();
			}
		});
		layer.on("popupclose", () => {
			this.saveDrawLayer();
			this.updateShapeLabel(layer);
		});
		layer.bindPopup(container, { className: 'shape-popup', maxWidth: 250 });
		this.updateShapeLabel(layer);
	}

	private updateShapeLabel(layer: L.Layer & { feature?: any }): void {
		const desc = layer.feature?.properties?.desc;
		if (desc && desc.trim()) {
			(layer as any).unbindTooltip?.();
			(layer as any).bindTooltip(desc, {
				permanent: true,
				direction: 'center',
				className: 'polygon-label'
			});
		} else {
			(layer as any).unbindTooltip?.();
		}
	}

	// ==================== CLICK BUFFER ====================

	addClickBuffer(line: L.Polyline): void {
		this.addClickBufferToLayer(line, this.polygons);
	}

	addClickBufferToLayer(line: L.Polyline, _targetGroup?: L.FeatureGroup): void {
		if (!this.map) return;

		if (typeof line.getLatLngs !== 'function') return;
		let latLngs = line.getLatLngs();
		if (!latLngs || (Array.isArray(latLngs) && latLngs.length === 0)) return;

		while (Array.isArray(latLngs) && latLngs.length > 0 && Array.isArray(latLngs[0]) && !('lat' in latLngs[0])) {
			latLngs = (latLngs as any[]).flat();
		}

		if (!latLngs || latLngs.length === 0) return;

		const layerId = (line as any)._zeichnungslayerId || this.activeZeichnungslayerId;
		if (!layerId) return;

		const panes = this.getZeichnungslayerPanes(layerId);

		const clickBuffer = L.polyline(latLngs as L.LatLng[], {
			weight: 20,
			color: 'transparent',
			opacity: 0,
			interactive: true,
			pane: panes.linien
		});

		clickBuffer.on('click', (e: L.LeafletMouseEvent) => {
			L.DomEvent.stopPropagation(e);
			line.fire('click', e);
		});

		clickBuffer.on('contextmenu', (e: L.LeafletMouseEvent) => {
			L.DomEvent.stopPropagation(e);
			line.fire('contextmenu', e);
		});

		clickBuffer.addTo(this.map);
		(line as any)._clickBuffer = clickBuffer;
	}

	// ==================== LINE DRAWING ====================

	async onToggleLine(): Promise<void> {
		if (this.lineEnabled) {
			this.endLineMode();
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'line';
			return;
		}

		this.activateLineMode();
	}

	private activateLineMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.lineEnabled = true;
		this.updateDrawToolActiveState();

		if (!this.activeZeichnungslayerId) return;
		const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);

		const layerStyle = this.getActiveLayerStyleSync();
		const style: L.PathOptions = {
			color: layerStyle.strokeColor,
			weight: layerStyle.strokeWidth
		};
		this.lineHandler = new (L.Draw as any).Polyline(this.map, {
			shapeOptions: { ...style, pane: panes.linien }
		});
		this.lineHandler?.enable();
	}

	private endLineMode(): void {
		if (this.lineHandler) {
			this.lineHandler?.disable();
			this.lineHandler = undefined;
			this.lineEnabled = false;
			this.updateDrawToolActiveState();
		}
	}

	// ==================== LINE FREEHAND ====================

	async onToggleLineFreehand(): Promise<void> {
		if (this.lineFreehandEnabled) {
			this.endLineFreehandMode();
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'lineFreehand';
			return;
		}

		this.activateLineFreehandMode();
	}

	private activateLineFreehandMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		this.onStopGpsTracking();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.lineFreehandEnabled = true;
		this.updateDrawToolActiveState();

		L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
		this.map.dragging.disable();

		this.map.on('mousedown', this.onLineFreehandMouseDown, this);
		this.map.on('mousemove', this.onLineFreehandMouseMove, this);
		this.map.on('mouseup', this.onLineFreehandMouseUp, this);

		const container = this.map.getContainer();
		container.addEventListener('touchstart', this.onLineFreehandTouchStart, { passive: false });
		container.addEventListener('touchmove', this.onLineFreehandTouchMove, { passive: false });
		container.addEventListener('touchend', this.onLineFreehandTouchEnd, { passive: false });
		container.addEventListener('contextmenu', this.onPreventContextMenu, { passive: false });
	}

	private endLineFreehandMode(): void {
		if (!this.lineFreehandEnabled) return;

		this.lineFreehandEnabled = false;
		this.lineFreehandDrawing = false;
		this.lineFreehandPoints = [];
		this.lastLineFreehandPoint = undefined;
		this.lineFreehandPolyline?.remove();
		this.lineFreehandPolyline = undefined;
		this.updateDrawToolActiveState();

		if (this.map) {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.dragging.enable();

			this.map.off('mousedown', this.onLineFreehandMouseDown);
			this.map.off('mousemove', this.onLineFreehandMouseMove);
			this.map.off('mouseup', this.onLineFreehandMouseUp);

			const container = this.map.getContainer();
			container.removeEventListener('touchstart', this.onLineFreehandTouchStart);
			container.removeEventListener('touchmove', this.onLineFreehandTouchMove);
			container.removeEventListener('touchend', this.onLineFreehandTouchEnd);
			container.removeEventListener('contextmenu', this.onPreventContextMenu);
		}
	}

	private onLineFreehandMouseDown(e: L.LeafletMouseEvent): void {
		if (!this.lineFreehandEnabled) return;
		this.startLineFreehandDrawing(e.latlng);
	}

	private onLineFreehandMouseMove(e: L.LeafletMouseEvent): void {
		if (!this.lineFreehandEnabled || !this.lineFreehandDrawing) return;
		this.continueLineFreehandDrawing(e.latlng, e.containerPoint);
	}

	private onLineFreehandMouseUp(e: L.LeafletMouseEvent): void {
		if (!this.lineFreehandEnabled || !this.lineFreehandDrawing) return;
		this.finishLineFreehandDrawing();
	}

	private onLineFreehandTouchStart = (e: TouchEvent): void => {
		if (!this.lineFreehandEnabled) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.startLineFreehandDrawing(latlng);
	}

	private onLineFreehandTouchMove = (e: TouchEvent): void => {
		if (!this.lineFreehandEnabled || !this.lineFreehandDrawing) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.continueLineFreehandDrawing(latlng, containerPoint);
	}

	private onLineFreehandTouchEnd = (e: TouchEvent): void => {
		if (!this.lineFreehandEnabled || !this.lineFreehandDrawing) return;

		e.preventDefault();
		this.finishLineFreehandDrawing();
	}

	private startLineFreehandDrawing(latlng: L.LatLng): void {
		this.lineFreehandDrawing = true;
		this.lineFreehandPoints = [latlng];
		this.lastLineFreehandPoint = latlng;

		if (!this.activeZeichnungslayerId) return;
		const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId);

		const layerStyle = this.getActiveLayerStyleSync();
		const style: L.PathOptions = {
			color: layerStyle.strokeColor,
			weight: layerStyle.strokeWidth
		};
		this.lineFreehandPolyline = L.polyline([latlng], { ...style, pane: panes.linien }).addTo(this.map);
	}

	private continueLineFreehandDrawing(latlng: L.LatLng, containerPoint: L.Point): void {
		if (!this.lastLineFreehandPoint || !this.lineFreehandPolyline) return;

		const lastContainerPoint = this.map.latLngToContainerPoint(this.lastLineFreehandPoint);
		const distance = containerPoint.distanceTo(lastContainerPoint);

		if (distance >= this.FREEHAND_MIN_DISTANCE) {
			this.lineFreehandPoints.push(latlng);
			this.lineFreehandPolyline.addLatLng(latlng);
			this.lastLineFreehandPoint = latlng;
		}
	}

	private async finishLineFreehandDrawing(): Promise<void> {
		this.lineFreehandDrawing = false;

		if (this.lineFreehandPoints.length >= 2) {
			if (!this.checkActiveZeichnungslayer()) {
				this.lineFreehandPolyline?.remove();
				this.lineFreehandPolyline = undefined;
				this.lineFreehandPoints = [];
				this.lastLineFreehandPoint = undefined;
				return;
			}
			const featureGroup = this.zeichnungen.get(this.activeZeichnungslayerId!);
			if (!featureGroup) {
				this.lineFreehandPolyline?.remove();
				this.lineFreehandPolyline = undefined;
				this.lineFreehandPoints = [];
				this.lastLineFreehandPoint = undefined;
				return;
			}

			const panes = this.getZeichnungslayerPanes(this.activeZeichnungslayerId!);

			const layerStyle = this.getActiveLayerStyleSync();
			const polyline = L.polyline(this.lineFreehandPoints, {
				color: layerStyle.strokeColor,
				weight: layerStyle.strokeWidth,
				pane: panes.linien
			});

			const feature = (polyline as any).feature = (polyline as any).feature || {};
			feature.type = feature.type || "Feature";
			const props = feature.properties = feature.properties || {};
			props.desc = '';
			props.style = {
				color: layerStyle.strokeColor,
				weight: layerStyle.strokeWidth
			};
			(polyline as any)._geomType = 'LineString';

			this.createShapePopup(polyline);

			featureGroup.addLayer(polyline);
			this.hasDrawnLine = true;

			if (this.zeichnungenLinien) {
				this.zeichnungenLinien.addLayer(polyline);
				if (this.map && !this.map.hasLayer(this.zeichnungenLinien)) {
					this.bridge.addLayerToMap('Zeichnungen_Linien', this.zeichnungenLinien, true);
				}
			}

			this.layerOrderingService.onLayerOrderChanged();

			this.addClickBufferToLayer(polyline);
			this.saveZeichnungslayer();

			this.lineFreehandPolyline?.remove();
			this.lineFreehandPolyline = undefined;
			this.lineFreehandPoints = [];
			this.lastLineFreehandPoint = undefined;
		} else {
			this.lineFreehandPolyline?.remove();
			this.lineFreehandPolyline = undefined;
			this.lineFreehandPoints = [];
			this.lastLineFreehandPoint = undefined;
		}
	}

	// ==================== GPS TRACKING ====================

	async onToggleGpsTrackingPolygon(): Promise<void> {
		if (this.gpsTrackingPolygonEnabled) {
			this.gpsTrackingPolygonEnabled = false;
			this.gpsUpdateTimer?.stopUpdating();
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'gpsTrackingPolygon';
			return;
		}

		this.activateGpsTrackingPolygonMode();
	}

	private activateGpsTrackingPolygonMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}
		this.gpsTrackingLineEnabled = false;

		this.gpsTrackingPolygonEnabled = true;

		this.requestWakeLock();
		this.gpsUpdateTimer?.startUpdating();
	}

	async onToggleGpsTrackingLine(): Promise<void> {
		if (this.gpsTrackingLineEnabled) {
			this.gpsTrackingLineEnabled = false;
			this.gpsUpdateTimer?.stopUpdating();
			this.pendingDrawTool = null;
			return;
		}

		if (!this.checkActiveZeichnungslayer()) {
			this.pendingDrawTool = 'gpsTrackingLine';
			return;
		}

		this.activateGpsTrackingLineMode();
	}

	private activateGpsTrackingLineMode(): void {
		this.onToolActivated?.();
		this.endNewPointOnMapMode();
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();
		this.mapSelectionService.cancelZoomToWindow();
		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}
		this.gpsTrackingPolygonEnabled = false;

		this.gpsTrackingLineEnabled = true;

		this.requestWakeLock();
		this.gpsUpdateTimer?.startUpdating();
	}

	onStopGpsTracking(): void {
		this.gpsTrackingPolygonEnabled = false;
		this.gpsTrackingLineEnabled = false;
		this.gpsUpdateTimer?.stopUpdating();
		this.releaseWakeLock();
	}

	private async requestWakeLock(): Promise<void> {
		if (this.wakeLock) return;

		try {
			if ('wakeLock' in navigator) {
				this.wakeLock = await navigator.wakeLock.request('screen');
				this.wakeLock.addEventListener('release', () => {
					this.wakeLock = null;
				});
			}
		} catch (err) {
			console.warn('Wake Lock konnte nicht aktiviert werden:', err);
		}
	}

	private async releaseWakeLock(): Promise<void> {
		if (this.wakeLock) {
			try {
				await this.wakeLock.release();
			} catch (err) {
				// Ignorieren
			}
			this.wakeLock = null;
		}
	}

	// ==================== VISIBILITY / FINISH ====================

	private onVisibilityChange = (): void => {
		if (document.visibilityState === 'hidden') {
			if (this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled) {
				if (this.currentTrackPolyline) {
					this.saveZeichnungslayer();
				}
				this.gpsTrackWasInterrupted = true;
			}
		} else if (document.visibilityState === 'visible') {
			if (this.gpsTrackWasInterrupted) {
				this.gpsTrackWasInterrupted = false;
				if (this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled) {
					this.msg.warning(
						'Die GPS-Aufzeichnung wurde möglicherweise unterbrochen, während die App im Hintergrund war.',
						'GPS-Tracking'
					);
				}
			}
			if (this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled) {
				this.requestWakeLock();
			}
		}
	};

	onFinishDrawMode(): void {
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();

		if (this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled) {
			this.onStopGpsTracking();
		}

		this.saveZeichnungslayer();

		this.hasDrawnPolygon = false;
		this.hasDrawnLine = false;
		this.pendingDrawTool = null;
	}

	onDeactivateAllTools(): void {
		this.endDrawMode();
		this.endFreehandMode();
		this.endLineMode();
		this.endLineFreehandMode();
		this.endEditMode();
		this.endDeleteMode();

		this.endNewPointOnMapMode();

		if (this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled) {
			this.onStopGpsTracking();
		}

		if (this.mapSelectionService.selectionMode !== 'none') {
			this.mapSelectionService.onSelectionModeChanged('none');
		}

		this.mapSelectionService.cancelZoomToWindow();

		this.hasDrawnPolygon = false;
		this.hasDrawnLine = false;
		this.pendingDrawTool = null;
	}
}
